/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { ILabelProps, ILabelStyles, Label } from '@fluentui/react/lib/Label';
import { Stack } from '@fluentui/react/lib/Stack';
import { ITextFieldProps, ITextFieldStyles } from '@fluentui/react/lib/TextField';
import { Toggle } from '@fluentui/react/lib/Toggle';
import { classNamesFunction } from '@fluentui/react/lib/Utilities';
import * as React from 'react';
import { defineMessages } from 'react-intl';
import { BasicModal, BasicModalProps } from 'src/core/components/BasicModal';
import { CalloutButton } from 'src/core/components/CalloutButton';
import { ExternalLink } from 'src/core/components/ExternalLink';
import { LuisDropdown } from 'src/core/components/LuisDropdown';
import { LuisTextField } from 'src/core/components/LuisTextField';
import { useFormState } from 'src/core/hooks/useFormState';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { globalMessages } from 'src/core/utils/globalMessages';
import { withModalMounter } from 'src/core/utils/hocs/withModalMounter';
import { PublishSlotType } from 'src/luis/types/PublishSlotType';

export type ChangeEndpointUrlParametersModalProps = BasicModalProps<ChangeEndpointUrlParametersModalDto> & {
    /**
     * The default data values of the params.
     */
    data: ChangeEndpointUrlParametersModalDto;
};

export type ChangeEndpointUrlParametersModalDto = {
    /**
     * The slot to deploy on.
     */
    slot: PublishSlotType;

    /**
     * Whether to show entity detailed response or not.
     */
    verbose: boolean;

    /**
     * Whether to show intent detailed response or not.
     */
    allIntents: boolean;

    /**
     * Whether to save logs or not.
     */
    log: boolean;

    /**
     * Whether spell check is enabled or not.
     */
    spellCheck: string;
};

const messages = defineMessages({
    title: {
        id: 'keys.azureCsAccountCard.title',
        defaultMessage: 'Change request parameters for this endpoint url'
    },
    learnMoreLuisC: {
        id: 'keys.azureCsAccountCard.learnMoreLuisC',
        defaultMessage: 'Learn more about LUIS request parameters.'
    },
    publishSlotLabel: {
        id: 'keys.azureCsAccountCard.publishSlotLabel',
        defaultMessage: 'Publishing slot'
    },
    timezoneLabel: {
        id: 'keys.azureCsAccountCard.timezoneLabel',
        defaultMessage: 'Timezone'
    },
    allIntentsLabel: {
        id: 'keys.azureCsAccountCard.allIntentsLabel',
        defaultMessage: 'Include scores for all intents'
    },
    verboseLabel: {
        id: 'keys.azureCsAccountCard.verboseLabel',
        defaultMessage: 'Include more entities details'
    },
    log: {
        id: 'keys.azureCsAccountCard.log',
        defaultMessage: 'Save logs'
    },
    spellCheckLabel: {
        id: 'keys.azureCsAccountCard.spellCheckLabel',
        defaultMessage: 'Override spell check, if enabled'
    },
    onText: {
        id: 'keys.azureCsAccountCard.onText',
        defaultMessage: 'On'
    },
    offText: {
        id: 'keys.azureCsAccountCard.offText',
        defaultMessage: 'Off'
    },
    enableSpellCheck: {
        id: 'keys.azureCsAccountCard.enableSpellCheck',
        defaultMessage: 'Enable spell check'
    },
    spellCheckPlaceholder: {
        id: 'keys.azureCsAccountCard.spellCheckPlaceholder',
        defaultMessage: 'Enter your spell check key'
    },
    spellCheckValidatorMessage: {
        id: 'keys.azureCsAccountCard.spellCheckValidatorMessage',
        defaultMessage: 'Please enter your spell check key'
    },
    spellCheckCallout: {
        id: 'keys.azureCsAccountCard.spellCheckCallout',
        defaultMessage:
            'You need to create a Bing Resource to enable this capability for the request being sent, then add the resource key here.'
    },
    learnMoreDetailed: {
        id: 'keys.azureCsAccountCard.learnMoreDetailed',
        defaultMessage: 'Learn more about the spell check.'
    }
});

const textFieldStyles = classNamesFunction<ITextFieldProps, ITextFieldStyles>()({
    root: {
        flex: 1
    },
    wrapper: {
        marginLeft: 12,
        marginRight: 12
    }
});

const spellerLabelStyles = classNamesFunction<ILabelProps, ILabelStyles>()({ root: { display: 'flex' } });

/**
 * Renders the modal responsible for changing the endpoint url parameters.
 */
export const ChangeEndpointUrlParametersModal = withModalMounter((props: ChangeEndpointUrlParametersModalProps) => {
    const {
        data: { slot, verbose, log, allIntents, spellCheck },
        onSubmit
    } = props;
    const { formState, formStateDto, updateFormState } = useFormState<ChangeEndpointUrlParametersModalDto>({
        slot: [slot],
        verbose: [verbose],
        log: [log],
        allIntents: [allIntents],
        spellCheck: [spellCheck]
    });
    const [isSpellCheckEnabled, setIsSpellCheckEnabled] = React.useState(spellCheck.length > 0);
    const { localize } = useLocalization();

    const isFormValid = formState.form.valid && (isSpellCheckEnabled ? formStateDto.spellCheck.length > 0 : true);

    const handleSubmit = () => {
        if (!isSpellCheckEnabled) {
            onSubmit({ ...formStateDto, spellCheck: '' });

            return;
        }
        onSubmit(formStateDto);
    };

    return (
        <BasicModal {...props} onSubmit={handleSubmit} valid={isFormValid} dialogContentProps={{ title: localize(messages.title) }}>
            <Stack tokens={{ childrenGap: 20 }}>
                <ExternalLink href="https://docs.microsoft.com/azure/cognitive-services/luis/luis-migration-api-v3#request-changes">
                    {localize(messages.learnMoreLuisC)}
                </ExternalLink>

                <LuisDropdown
                    isSearchEnabled={false}
                    selectedKey={formState.fields.slot.value}
                    label={localize(messages.publishSlotLabel)}
                    onChange={(_, selectedSlot) => updateFormState({ slot: selectedSlot.key as PublishSlotType })}
                    options={[
                        { key: 'staging', text: 'Staging' },
                        { key: 'production', text: 'Production' }
                    ]}
                />

                <Toggle
                    onText={localize(messages.onText)}
                    offText={localize(messages.offText)}
                    label={localize(messages.allIntentsLabel)}
                    checked={formState.fields.allIntents.value}
                    onChange={(_, checked) => updateFormState({ allIntents: checked })}
                />

                <Toggle
                    onText={localize(messages.onText)}
                    offText={localize(messages.offText)}
                    label={localize(messages.verboseLabel)}
                    checked={formState.fields.verbose.value}
                    onChange={(_, checked) => updateFormState({ verbose: checked })}
                />

                <Toggle
                    onText={localize(messages.onText)}
                    offText={localize(messages.offText)}
                    label={localize(messages.log)}
                    checked={formState.fields.log.value}
                    onChange={(_, checked) => updateFormState({ log: checked })}
                />

                <Stack verticalAlign="baseline">
                    <Label styles={spellerLabelStyles}>
                        {localize(messages.enableSpellCheck)}
                        <CalloutButton
                            content={
                                <span>
                                    {localize(messages.spellCheckCallout)}
                                    <ExternalLink
                                        isDarkMode
                                        aria-label={localize(messages.learnMoreDetailed)}
                                        href="https://docs.microsoft.com/azure/cognitive-services/luis/luis-tutorial-bing-spellcheck">
                                        {localize(globalMessages.learnMore)}
                                    </ExternalLink>
                                </span>
                            }
                        />
                    </Label>

                    <Stack horizontal horizontalAlign="baseline" verticalAlign="baseline">
                        <Toggle
                            onText={localize(messages.onText)}
                            offText={localize(messages.offText)}
                            checked={isSpellCheckEnabled}
                            onChange={(_, checked) => setIsSpellCheckEnabled(checked)}
                        />

                        <LuisTextField
                            styles={textFieldStyles}
                            disabled={!isSpellCheckEnabled}
                            placeholder={localize(messages.spellCheckPlaceholder)}
                            value={formState.fields.spellCheck.value}
                            onChange={(_, newVal) => updateFormState({ spellCheck: newVal })}
                        />
                    </Stack>
                </Stack>
            </Stack>
        </BasicModal>
    );
});
